import { LitElement, html, css, createRef, ref } from '@hh/global.js'

class HHInputWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: {
      reflect: true,
    },
    filled: {
      reflect: true,
    },
    label: {},
    counter: { type: Boolean },
    _inputCharCount: { type: Number, state: true },
    _totalCharCount: { type: Number, state: true },
  }

  constructor() {
    super()

    this._inputCharCount = 0
    this._totalCharCount = 0
  }

  connectedCallback() {
    super.connectedCallback()

    this.$input = this.querySelector('[slot="input"]')
    this.$select = this.querySelector(`select[slot='input']`)
    this.$select && this.preselectSelectValue()

    this.counter && this.initCounter()

    if (!!this.querySelector(`[slot='input']`).value && !this.$select) {
      this.filled = true
    }

    if (this.$select && this.label !== 'stacked') {
      this.filled = true
    }

    if (this.textArea) {
      this.resizeObserver = new ResizeObserver(() => this._setTextareaHeight())

      this.updateComplete.then(() => {
        this._setTextareaHeight()
        this.resizeObserver.observe(this.textArea)
      })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.resizeObserver.unobserve(this.textArea)
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default
    }
  }

  onChange(e) {
    if (this.animation === 'none' || this.label === 'stacked') {
      return
    }
    this.filled = !!e.target.value

    this._setTextareaHeight()
  }

  onTextareaChange(e) {
    this._setTextareaHeight()
  }

  onInput(e) {
    this.counter && this.countCharacters(e)
    this.textArea && this._setTextareaHeight()
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = true
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return
    }

    this.focused = false
  }

  initCounter() {
    if (!this.$input.maxLength) {
      this.counter = false
    }

    this._totalCharCount = this.$input.maxLength
  }

  countCharacters(event) {
    this._inputCharCount = event.target.value.length
  }

  _setTextareaHeight() {
    if (!this.textArea) return
    const scrollHeight = this.textArea.scrollHeight
    this.style.height = 'auto'
    this.textArea.style.height = 'auto'
    this.style.height = `${scrollHeight}px`
    this.textArea.style.height = `${scrollHeight}px`
  }

  get textArea() {
    return this.querySelector('textarea') ?? null
  }

  render() {
    return html`
      <slot name="label"></slot>
      <slot name="icon"></slot>
      <slot
        name="input"
        @change="${this.onChange}"
        @input="${this.onInput}"
        @keydown="${this.onChange}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
      ></slot>

      ${this.counter &&
      html`
        <span id="counter">
          <span id="current-count">${this._inputCharCount}</span>/<span
            id="total-count"
            >${this._totalCharCount}</span
          >
        </span>
      `}
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    ::slotted(label) {
      position: absolute;
      height: var(--hh-height, 45px);
      line-height: var(--hh-height, 45px);
      padding-left: var(--hh-left-indent-small, 10px);
      transform: translateY(-1px);
      transition: transform var(--hh-duration, 300ms),
        font-size var(--hh-duration, 300ms);
      font-size: var(--hh-placeholder-font-size);
      color: var(--hh-placeholder-color, #000);
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      width: 100%;
      min-height: var(--hh-height, 45px);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      z-index: 2;
      text-indent: var(--hh-left-indent-small, 10px);
      font: var(--hh-font);
      color: currentColor;
      background: transparent;
      border: 0;
      padding: 5px 0 0;
      font: var(--hh-font);
    }

    ::slotted(textarea) {
      box-sizing: border-box !important;
      position: static;
      padding: 20px var(--hh-left-indent-small, 10px) 0;
      text-indent: unset;
      resize: none;
    }

    ::slotted(select) {
      appearance: none;
    }

    ::slotted(input[type='date'])::-webkit-inner-spin-button,
    ::slotted(input[type='date'])::-webkit-calendar-picker-indicator {
      opacity: 0;
      display: none;
      appearance: none;
      -webkit-appearance: none;
    }

    :host ::slotted([slot='label']) {
      font-size: var(--hh-placeholder-font-size);
      color: var(--hh-placeholder-color, #000);
    }

    :host([focused='true']) ::slotted(label),
    :host([filled='true']) ::slotted(label) {
      font-size: var(--hh-minified-font-size, 9px);
      transform: translateY(-13px);
    }

    :host([theme='no-floating-label']) ::slotted(label) {
      font-size: var(--hh-minified-font-size, 9px);
      transform: translateY(-13px);
      padding-left: 1.4rem;
    }

    :host([animation='disappear'][focused='true']) ::slotted(label),
    :host([animation='disappear'][filled='true']) ::slotted(label) {
      visibility: hidden;
      transform: translateY(0);
      font-size: inherit;
    }

    :host {
      position: relative;
      display: block;
      min-height: calc(var(--hh-height, 45px) + 2px);
    }

    :host {
      background: transparent;
      border: var(--hh-border-size, 1px) solid currentColor;
    }

    ::slotted(*) {
      outline: 0 !important;
      outline-offset: 0 !important;
    }

    :host([focused='true']) {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    :host([theme='minimal']) {
      background: transparent;
      border: none;
      border-bottom: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([label='stacked']) {
      border: none;
      height: auto;
    }

    :host([label='stacked']) ::slotted(label),
    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      position: relative;
    }

    :host([label='stacked']) ::slotted(label) {
      height: auto;
      display: block;
      font: var(--hh-label-font);
      margin: var(--hh-label-margin);
      padding-left: 0;
    }

    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      border: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--hh-left-indent, 32px);
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--hh-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--hh-right-indent, 32px);
    }

    ::slotted([slot='icon']) {
      display: flex;
      align-items: center;
      pointer-events: none;
      position: absolute;
      width: var(--hh-icon-width, 23px);
      height: var(--hh-height, 45px);
      z-index: 3;
    }

    :host([icon='right']) ::slotted([slot='icon']) {
      right: var(--hh-icon-right, 10px);
    }

    :host([height='multiline']),
    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='compact']),
    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--hh-compact-height, 35px);
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--hh-compact-height, 35px);
    }

    #counter {
      position: absolute;
      top: 50%;
      right: 1.5rem;
      transform: translateY(-50%);
    }
  `
}

customElements.define('hh-input-wrapper', HHInputWrapper)
